import React, { useMemo, useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { Col, Form, Row, Modal, Table, Tag, Spin } from "antd";
import { useHistory } from "react-router-dom";
import Active from "./Active";
import queryKeys from "../../common/queryKey";
import { useSelector } from "react-redux";
import { useScrollFetch } from "../../hooks/useScrollFetch";
import { Menu, MenuItem } from "@mui/material";
import { utils, writeFileXLSX, read } from "xlsx";
import { getFilters, getClientid, getMenuList, getUserRoleId } from "redux/selectors";
import { ButtonFilled, ButtonOutlined } from "reusableComponent/UIButtons";
import { CALL_API_UPDATED } from "../../common/API";
import { MAIN_CLIENT_ID } from "../../common/Constants";
import { hasClientPermission } from "./clientPermission";

const ClientWrapStyle = styled.div`
  padding: 20px;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const BASE_TEMPLATE_HEADERS = [
    "Company Registered Name*",
    "Managing Office*",
    "Relationship Type*",
    "Primary Contact Person*",
    "Primary Contact Email*",
    "Influential Star Customer* (Yes/No)",
    "Current Nature of Engagement*",
    "Login Office Email*",
    "Login Password*",
    "BC Client ID*",
    "Risk Classification*",
    "Review Cycle Period* (Annual/Quarterly/Monthly)",
    "Status* (Active/Inactive)",
    "Last Date Included in Campaign*",
    "BR Flag Value*",
    "Designation Template*",
    "Created Date*",
    "Archive Reason*",
    "Archive Date*",
    "Strategic Partner* (Yes/No)",
    "Business Designation Type*",
    "Designation Level / Location Hierarchy*",
    "Owner",
    "Company Email",
    "Registration No",
    "Country",
    "City",
    "Website URL",
    "Description",
];

// Fields removed when the logged-in client is NOT the main client
const NON_MAIN_EXCLUDED_HEADERS = ["Login Office Email*", "Login Password*"];

// Main client: full template.
// Any other client: no login fields, and "BC Client ID*" becomes "System Client ID*".
const getTemplateHeaders = (isMainClient) => {
    if (isMainClient) return BASE_TEMPLATE_HEADERS;
    return BASE_TEMPLATE_HEADERS.filter(
        (h) => !NON_MAIN_EXCLUDED_HEADERS.includes(h)
    ).map((h) => (h === "BC Client ID*" ? "System Client ID*" : h));
};

// Matches an uploaded file's header cell to a known template header regardless of
// case, extra whitespace, or a missing/extra trailing "*" (required-field marker) -
// small formatting differences a user can easily introduce when typing or copying
// header text by hand.
const normalizeHeader = (h) =>
    h
        .toString()
        .trim()
        .toLowerCase()
        .replace(/\*+\s*$/, "")
        .replace(/\s+/g, " ")
        .trim();

const HEADER_TO_FIELD = {
    "Company Registered Name*": "companyRegisteredName",
    "Managing Office*": "managingOffice",
    "Relationship Type*": "relationshipType",
    "Primary Contact Person*": "primaryContactPerson",
    "Primary Contact Email*": "primaryContactEmail",
    "Influential Star Customer* (Yes/No)": "influentialstarCustomer",
    "Current Nature of Engagement*": "currentNatureofEngag",
    "Login Office Email*": "loginOfficeEmail",
    "Login Password*": "loginPassword",
    "BC Client ID*": "bcClientId",
    "System Client ID*": "bcClientId", // non-main client label for the same field
    "Risk Classification*": "riskClassification",
    "Review Cycle Period* (Annual/Quarterly/Monthly)": "reviewCyclePeriod",
    "Status* (Active/Inactive)": "status",
    "Last Date Included in Campaign*": "lastDateCampaign",
    "BR Flag Value*": "brFlagValue",
    "Designation Template*": "designationTemplate",
    "Created Date*": "createdDate",
    "Archive Reason*": "archiveReason",
    "Archive Date*": "archiveDate",
    "Strategic Partner* (Yes/No)": "strategicPartner",
    "Business Designation Type*": "businessDesignationType",
    "Designation Level / Location Hierarchy*": "designationLevel",
    "Owner": "owner",
    "Company Email": "emailaddress",
    "Registration No": "bdoRegistrationno",
    "Country": "country",
    "City": "address1City",
    "Website URL": "companyWebsiteUrl",
    "Description": "description",
};

const NORMALIZED_HEADER_TO_FIELD = Object.entries(HEADER_TO_FIELD).reduce(
    (acc, [header, field]) => {
        acc[normalizeHeader(header)] = field;
        return acc;
    },
    {}
);

const ClientManagement = () => {
    const [form] = Form.useForm();
    const history = useHistory();
    const filters = useSelector(getFilters);

    // Same logic as the General Information page
    const clientId = useSelector(getClientid);
    const isMainClient = clientId === MAIN_CLIENT_ID;

    // Menu permissions for the logged-in user's role
    const menuList = useSelector(getMenuList);
    const userRoleId = useSelector(getUserRoleId);
    const Menulist = menuList?.[userRoleId] || Object.values(menuList || {})[0];

    // Create permission from the "client" or "ownclient" menu
    const hasClientCreatePermission = hasClientPermission(Menulist, "create");

    const [anchorEl, setAnchorEl] = useState(null);
    const fileInputRef = useRef(null);
    const [importModal, setImportModal] = useState({ open: false, loading: false, result: null });

    const [exportData, setExportData] = useState([]);

    const xport = React.useCallback(() => {
        if (!exportData.length) return;

        const ws = utils.json_to_sheet(exportData);
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "Report");
        writeFileXLSX(wb, "Client Report.xlsx");
    }, [exportData]);

    const downloadTemplate = () => {
        const headers = getTemplateHeaders(isMainClient);
        const ws = utils.aoa_to_sheet([headers]);
        ws["!cols"] = headers.map(() => ({ wch: 30 }));
        const wb = utils.book_new();
        utils.book_append_sheet(wb, ws, "Client Import Template");
        writeFileXLSX(wb, "Client_Import_Template.xlsx");
    };

    const handleImportFile = async (e) => {
        const file = e.target.files[0];
        if (!file || !hasClientCreatePermission) return;
        fileInputRef.current.value = "";

        const buffer = await file.arrayBuffer();
        // cellDates: true → Excel date serials become JS Date objects
        const wb = read(buffer, { cellDates: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = utils.sheet_to_json(ws, { defval: "" });

        // native <input type="date"> needs YYYY-MM-DD; format any Date cells
        const toYMD = (d) => {
            const yyyy = d.getUTCFullYear();
            const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
            const dd = String(d.getUTCDate()).padStart(2, "0");
            return `${yyyy}-${mm}-${dd}`;
        };

        const clients = rows
            .map((row) => {
                const mapped = {};
                for (const [rawHeader, value] of Object.entries(row)) {
                    const field = NORMALIZED_HEADER_TO_FIELD[normalizeHeader(rawHeader)];
                    if (!field) continue;
                    mapped[field] =
                        value instanceof Date ? toYMD(value) : String(value ?? "").trim();
                }
                return mapped;
            })
            .filter((c) => Object.values(c).some((v) => v));

        if (clients.length === 0) {
            setImportModal({ open: true, loading: false, result: { message: "No data rows found in file.", success: [], errors: [] } });
            return;
        }

        setImportModal({ open: true, loading: true, result: null });

        try {
            const r = await CALL_API_UPDATED(
                "insyts-crm-clients?bulkImport=true",
                "post",
                { clients }
            );
            setImportModal({ open: true, loading: false, result: r?.response });
            // refresh the list so freshly imported clients show without a manual reload
            refetch();
        } catch (err) {
            setImportModal({ open: true, loading: false, result: { message: "Import failed. Please try again.", success: [], errors: [] } });
        }
    };

    const handleClick = (event) => setAnchorEl(event.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const {
        getownClientsList,
        getClientsName,
        getManagingOffice,
        getClientStatus,
        getClientOwner,
        getRelationshipManager,
    } = queryKeys;
    const { key, url } = getownClientsList(filters?.client?.managingoffice ?? []);

    const { data, isLoading, isReloading, refetch, fetchNextPage, hasNextPage, isFetching } = useScrollFetch(
        key,
        {
            endPoint: url,
            queryParam: "LastEvaluatedKey",
        },
        {
            // this endpoint's payload is wrapped as { status, response: {...} }
            // (see returnResponse() on the backend) - the cursor lives at
            // response.LastEvaluatedKey, not at the top level the hook defaults to
            getNextPageParam: (lastPage) => lastPage?.response?.LastEvaluatedKey || null,
        }
    );

    // The list uses page-number pagination over the full client set, so every
    // page is pulled in up front (in the background) instead of waiting for
    // the user to scroll - the backend only returns 20 records per request.
    useEffect(() => {
        if (hasNextPage && !isFetching) {
            fetchNextPage();
        }
    }, [hasNextPage, isFetching, fetchNextPage]);

    const dataList = useMemo(() => {
        if (!data?.pages?.length) return [];

        const list = data.pages.reduce((acc, page) => {
            const pageList = page?.response?.clientName || [];
            return [...acc, ...pageList];
        }, []);

        // order by recently created (newest first)
        return [...list].sort((a, b) => {
            const da = a?.createdon ? new Date(a.createdon).getTime() : 0;
            const db = b?.createdon ? new Date(b.createdon).getTime() : 0;
            return db - da;
        });
    }, [data]);

    const totalFilteredClientCount = data?.pages?.[0]?.response?.count;

    const handleCopy = () => {
        navigator.clipboard.writeText(`${window.location.origin}/clientonboard`);
    };

    const importResultColumns = [
        { title: "Row", dataIndex: "row", width: 60 },
        { title: "Company Name", dataIndex: "name" },
        { title: "Client ID / Error", dataIndex: "clientId", render: (_, r) => r.clientId || <span style={{ color: "#ff4d4f" }}>{r.error}</span> },
    ];

    return (
        <ClientWrapStyle>
            <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                style={{ display: "none" }}
                onChange={handleImportFile}
            />

            <Modal
                title="Bulk Import Results"
                open={importModal.open}
                onCancel={() => setImportModal({ open: false, loading: false, result: null })}
                footer={null}
                width={640}
            >
                {importModal.loading ? (
                    <div style={{ textAlign: "center", padding: 40 }}>
                        <Spin size="large" />
                        <p style={{ marginTop: 16 }}>Importing clients, please wait...</p>
                    </div>
                ) : importModal.result ? (
                    <div>
                        <p style={{ marginBottom: 16 }}>{importModal.result.message}</p>
                        {importModal.result.success?.length > 0 && (
                            <>
                                <Tag color="green" style={{ marginBottom: 8 }}>
                                    {importModal.result.success.length} Imported Successfully
                                </Tag>
                                <Table
                                    size="small"
                                    dataSource={importModal.result.success}
                                    columns={importResultColumns}
                                    rowKey="row"
                                    pagination={false}
                                    style={{ marginBottom: 16 }}
                                />
                            </>
                        )}
                        {importModal.result.errors?.length > 0 && (
                            <>
                                <Tag color="red" style={{ marginBottom: 8 }}>
                                    {importModal.result.errors.length} Failed
                                </Tag>
                                <Table
                                    size="small"
                                    dataSource={importModal.result.errors}
                                    columns={importResultColumns}
                                    rowKey="row"
                                    pagination={false}
                                />
                            </>
                        )}
                    </div>
                ) : null}
            </Modal>

            <Row justify="space-between" align="middle" className="mb-3">
                <Col>
                    <div className="page-title">Client</div>
                </Col>
                <Col style={{ display: "flex", gap: 8 }}>
                    {/* Download Template, Import and Add New all create clients,
                        so they are shown only with create permission */}
                    {hasClientCreatePermission && (
                        <>
                            <ButtonOutlined onClick={downloadTemplate}>
                                Download Template
                            </ButtonOutlined>
                            <ButtonOutlined onClick={() => fileInputRef.current?.click()}>
                                Import Clients
                            </ButtonOutlined>
                            <ButtonFilled onClick={() => history.push("/add-client")}>
                                Add New
                            </ButtonFilled>
                        </>
                    )}

                </Col>
            </Row>

            <Active
                clientList={dataList}
                showLoader={isLoading}
                loader={isReloading}
                totalFilteredClientCount={totalFilteredClientCount}
                onSendData={setExportData}
            />
        </ClientWrapStyle>
    );
};

export default ClientManagement;